import { useEffect, useRef, useState } from 'react';

import * as graphql from '@treams/treams-network';

import { createAudioSource } from './feedbackAudioSource';
import { appendTranscript, feedbackStreamingUrl } from './feedbackVoiceTranscript';

type Delta = { ops: { insert?: unknown; attributes?: unknown }[] };

type IncomingMessage = {
	type?: string;
	end_of_turn?: boolean;
	turn_is_formatted?: boolean;
	transcript?: string;
};

/**
 * Streams one speaker into the answer field. A partial turn replaces the
 * uncommitted tail. A formatted end of turn keeps it. The socket is not
 * retried: a failure leaves the text already written.
 */
export function useFeedbackVoiceStream(input: {
	language: graphql.TranscriptionLanguage;
	responseID?: string;
	text: Delta | null | undefined;
	onText: (text: Delta) => void;
	onError: () => void;
}) {
	const [recording, setRecording] = useState(false);
	const [partial, setPartial] = useState('');
	const textRef = useRef(input.text);
	const baseRef = useRef<Delta | null | undefined>(input.text);
	const partialRef = useRef('');
	const socketRef = useRef<WebSocket | null>(null);
	const mediaStreamRef = useRef<MediaStream | null>(null);
	const audioStopRef = useRef<(() => void) | null>(null);
	const startingRef = useRef(false);
	const sessionRef = useRef(0);
	const stoppingRef = useRef(false);
	const onTextRef = useRef(input.onText);
	const onErrorRef = useRef(input.onError);
	textRef.current = input.text;
	onTextRef.current = input.onText;
	onErrorRef.current = input.onError;

	const [startMutation] = graphql.useStartFeedbackVoiceTranscriptionMutation();

	function release() {
		sessionRef.current += 1;
		partialRef.current = '';
		audioStopRef.current?.();
		audioStopRef.current = null;
		mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
		mediaStreamRef.current = null;
		const socket = socketRef.current;
		socketRef.current = null;
		if (socket) {
			socket.onmessage = null;
			socket.onerror = null;
			socket.onclose = null;
			socket.close();
		}
		startingRef.current = false;
	}

	const releaseRef = useRef(release);
	releaseRef.current = release;

	useEffect(() => () => releaseRef.current(), []);

	function publish(nextBase: Delta, nextPartial: string) {
		baseRef.current = nextBase;
		partialRef.current = nextPartial;
		setPartial(nextPartial);
		onTextRef.current(nextPartial ? appendTranscript(nextBase, nextPartial) : nextBase);
	}

	function stop() {
		stoppingRef.current = true;
		release();
		stoppingRef.current = false;
		setRecording(false);
		setPartial('');
	}

	function stillCurrent(session: number) {
		return sessionRef.current === session;
	}

	async function start() {
		if (startingRef.current || socketRef.current) {
			return;
		}

		const session = sessionRef.current + 1;
		sessionRef.current = session;
		startingRef.current = true;
		baseRef.current = textRef.current;
		partialRef.current = '';
		setRecording(true);
		setPartial('');

		try {
			const result = await startMutation({
				variables: {
					input: {
						language: input.language,
						responseID: input.responseID,
					},
				},
			});
			if (!stillCurrent(session)) {
				return;
			}

			const token = result.data?.startFeedbackVoiceTranscription.token;
			if (!token) {
				throw new Error('Missing streaming token');
			}

			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			if (!stillCurrent(session)) {
				stream.getTracks().forEach((track) => track.stop());
				return;
			}
			mediaStreamRef.current = stream;
			const track = stream.getAudioTracks?.()[0];
			if (!track) {
				throw new Error('The microphone stream has no audio track');
			}

			track.addEventListener?.('ended', () => {
				if (stoppingRef.current || !stillCurrent(session)) {
					return;
				}
				onErrorRef.current();
				stop();
			});

			const source = await createAudioSource(stream, {
				onLevel: () => undefined,
				onError: () => {
					if (!stillCurrent(session)) {
						return;
					}
					onErrorRef.current();
					stop();
				},
			});
			if (!stillCurrent(session)) {
				source.stop();
				stream.getTracks().forEach((item) => item.stop());
				return;
			}
			audioStopRef.current = () => source.stop();

			const socket = new WebSocket(
				feedbackStreamingUrl({
					token,
					language: input.language,
					encodingParams: source.encodingParams,
				}),
			);
			socket.binaryType = 'arraybuffer';
			if (!stillCurrent(session)) {
				socket.close();
				source.stop();
				stream.getTracks().forEach((item) => item.stop());
				return;
			}
			socketRef.current = socket;

			socket.onmessage = (event) => {
				if (socketRef.current !== socket) {
					return;
				}

				if (typeof event.data !== 'string') {
					return;
				}

				let message: IncomingMessage;
				try {
					message = JSON.parse(event.data) as IncomingMessage;
				} catch {
					return;
				}

				if (message.type === 'Termination') {
					if (socketRef.current === socket) {
						stop();
					}
					return;
				}

				if (message.type !== 'Turn' || !message.transcript) {
					return;
				}

				if (!message.end_of_turn) {
					if (!partialRef.current) {
						baseRef.current = textRef.current;
					}
					publish(baseRef.current ?? { ops: [] }, message.transcript);
					return;
				}

				if (!message.turn_is_formatted) {
					return;
				}

				const base = partialRef.current ? baseRef.current : textRef.current;
				publish(appendTranscript(base, message.transcript), '');
			};

			socket.onerror = () => {
				if (socketRef.current !== socket) {
					return;
				}
				onErrorRef.current();
				stop();
			};

			socket.onclose = () => {
				if (socketRef.current !== socket || stoppingRef.current) {
					return;
				}
				onErrorRef.current();
				stop();
			};

			source.setSink((frame) => {
				if (socket.readyState === WebSocket.OPEN) {
					socket.send(frame);
				}
			});
		} catch {
			if (!stillCurrent(session)) {
				return;
			}
			release();
			setRecording(false);
			setPartial('');
			onErrorRef.current();
		}
	}

	return { recording, partial, start, stop };
}
